import styled from '@emotion/styled'
import { useReducer } from 'react'
import { useTranslation } from 'react-i18next'

import { useTypedDispatch, useTypedSelector } from '..'
import { sendFeedback } from '../actions/feedback'
import ActionError from '../components/ActionError'
import Annotated from '../components/annotated/Annotated'
import { SendButton } from '../components/button/Button'
import ButtonLink from '../components/button/ButtonLink'
import {
  draftReducer,
  initialDraft,
  isBadRating,
  toFeedbackTarget,
  toNewFeedback,
} from '../components/feedback/feedbackDraft'
import Header from '../components/header/Header'
import StyledInput from '../components/Input'
import Label from '../components/Label'
import LoadingIndicator from '../components/LoadingIndicator'
import { FooterPage } from '../components/page/Page'
import Rating from '../components/rating/Rating'
import RatingButtons from '../components/rating/RatingButtons'
import SelectableRow from '../components/SelectableRow'
import Text from '../components/Text'
import VerticalLayout from '../components/VerticalLayout'
import { MOMENT_SHIFT_DATE_FORMAT, MOMENT_SHIFTLIST_DATE_FORMAT } from '../constants'
import moment from '../lib/moment-fi'
import { scheduleURL, shiftURL, visitURL } from '../lib/url'
import { selectFeedback, selectSendStatus } from '../reducers/feedbackSlice'
import { shiftsAndRestDaysSortedByDateSelector } from '../Selectors'
import { defaultTextColor, getColor, theme } from '../Theme'
import type { AppState, RestDay, Shift } from '../types'
import type { FeedbackType } from '../types/Feedback'

const TEXTAREA_MAX_LENGTH = 512

const ErrorTitle = styled.div`
  ${theme.text('largest', 'decorative', 'bold')};
  color: ${defaultTextColor};
  margin: ${theme.spacing.sizes.huge} 0;
  max-width: 100%;
  text-align: center;
`

const Container = styled.div`
  ${theme.layout.fluidWidth(theme.maxWidths.content)};
  /* Add room for scroll input into view */
  margin-bottom: 50vh;
`

type FeedbackAnswerProps = {
  isMaxLength: boolean
  maxLength: number
}

const FeedbackAnswerInput = styled(StyledInput)<FeedbackAnswerProps>`
  &:focus,
  &:focus-visible {
    outline: ${(p) => (p.isMaxLength ? 'none' : 'red')};
    border-color: ${(p) =>
      p.isMaxLength
        ? getColor(p.theme, ['red'], ['red'])
        : getColor(p.theme, ['nightGray'], ['white'])};
    box-shadow: 0 0 0 1px
      ${(p) =>
        p.isMaxLength
          ? getColor(p.theme, ['red'], ['red'])
          : getColor(p.theme, ['nightGray'], ['white'])};
  }
`

const WarningText = styled(Text)`
  color: ${(p) => getColor(p.theme, ['red'], ['red'])};
`

const FooterContainer = styled.div`
  ${theme.layout.fluidWidth(theme.maxWidths.content)};
  height: 100%;
`

const Constrain = styled.div`
  ${theme.layout.fluidWidth(theme.maxWidths.paddedContent)};
`

const Row = styled.div`
  ${theme.layout.flexRow};
  ${theme.spacing.all('large')};
  background: ${(p) => getColor(p.theme, ['white'], ['nightBlack'])};
  color: ${defaultTextColor};
`

type Reason = { title: string; value: string }

const shiftEmphasisReasons: Reason[] = [
  { title: 'rest', value: 'lepo' },
  { title: 'duration', value: 'pituus' },
  { title: 'tasks', value: 'sisalto' },
  { title: 'shiftList', value: 'sijoittuminen' },
  { title: 'startDateTime', value: 'alkamisaika' },
  { title: 'endDateTime', value: 'paattymisaika' },
]

const scheduleEmphasisReasons: Reason[] = [
  { title: 'scheduleRotation', value: 'kiertosuunta' },
  { title: 'amountOfFreeTime', value: 'vapaanmaara' },
  { title: 'freeTimeRequests', value: 'vapaatoiveet' },
  { title: 'pacing', value: 'rytmitys' },
  { title: 'fairness', value: 'tasapuolisuus' },
]

const shiftErrorReasons: Reason[] = [
  { title: 'startDateTime', value: 'aloitusaika' },
  { title: 'endDateTime', value: 'lopetusaika' },
  /* // Missing in eSäLLi?
  { title: 'extraTasks', value: 'ylimaarainentehtava' },*/
  { title: 'wrongTaskTimes', value: 'tehtavanajatvaarin' },
  { title: 'missingTasks', value: 'tehtavapuuttuu' },
]

const scheduleErrorReasons: Reason[] = [
  { title: 'scheduleContinuity', value: 'jaksonloppu' },
  { title: 'tooShortBreaks', value: 'lyhytvali' },
  { title: 'missingShift', value: 'tyovuoropuuttuu' },
  { title: 'notQualified', value: 'eipatevyytta' },
  { title: 'invalidAbsense', value: 'poissaolovaarin' },
]

type LimitedTextareaProps = {
  value: string
  disabled: boolean
  onChange: (value: string) => void
}

const LimitedTextarea = ({ value, disabled, onChange }: LimitedTextareaProps) => {
  const { t } = useTranslation()
  const isMaxLength = value.length >= TEXTAREA_MAX_LENGTH

  return (
    <Annotated title={t('feedbackAnswer.somethingElse')} horizontal={false}>
      <FeedbackAnswerInput
        rows={2}
        value={value}
        disabled={disabled}
        maxLength={TEXTAREA_MAX_LENGTH}
        isMaxLength={isMaxLength}
        onChange={onChange}
      />
      {isMaxLength ? (
        <WarningText>
          {t('feedbackAnswer.textareaMaxLength', { maxLength: TEXTAREA_MAX_LENGTH })}
        </WarningText>
      ) : (
        <></>
      )}
    </Annotated>
  )
}

const selectTargetShift = (
  state: AppState,
  scope: FeedbackType,
  id: string
): Shift | RestDay | null =>
  scope === 'schedule'
    ? shiftsAndRestDaysSortedByDateSelector(state).find((s) => s.listId === id) ?? null
    : state.shifts.byId[id] ?? null

const listDates = (shift: Shift | RestDay): string => {
  const end = shift.listEndDate
    ? moment(shift.listEndDate)
    : moment(shift.listStartDate).add(20, 'days')
  return `${moment(shift.listStartDate).format(MOMENT_SHIFTLIST_DATE_FORMAT)} – ${end.format(
    MOMENT_SHIFTLIST_DATE_FORMAT
  )}`
}

/* eslint-disable @typescript-eslint/no-empty-function */
const noop = () => {}

const FeedbackPage = () => {
  const { t } = useTranslation()
  const dispatch = useTypedDispatch()

  const scope = useTypedSelector((state) =>
    state.found.match.location.pathname.includes('list-feedback') ? 'schedule' : 'shift'
  )
  const initialStars = useTypedSelector(
    (state) => parseInt(String(state.found.match.location.query.initialStars), 10) || 0
  )
  const routeId = useTypedSelector((state) =>
    decodeURIComponent(String(state.found.match.params.targetId))
  )
  const shift = useTypedSelector((state) => selectTargetShift(state, scope, routeId))
  const shiftsLoading = useTypedSelector((state) => state.shifts.loading)
  const target = toFeedbackTarget(scope, shift)
  const sentFeedback = useTypedSelector((state) => selectFeedback(state, target?.targetId))
  const sendStatus = useTypedSelector((state) => selectSendStatus(state, target?.targetId))

  const [draft, dispatchDraft] = useReducer(draftReducer, initialStars, initialDraft)

  if (!shift || !target) {
    return (
      <FooterPage
        footer={
          <FooterContainer>
            <ButtonLink topOpen={false} bottomOpen={true} to={scheduleURL()}>
              {t('close')}
            </ButtonLink>
          </FooterContainer>
        }
        footerHeight={theme.maxHeights.footer}
      >
        {shiftsLoading ? (
          <LoadingIndicator padded={true} size="normal" />
        ) : (
          <ErrorTitle>{t('errors.general.notFound')}</ErrorTitle>
        )}
      </FooterPage>
    )
  }

  const closeUrl = target.type === 'shift' ? shiftURL(target.targetId) : scheduleURL()
  const values = sentFeedback ?? draft
  const sent = !!sentFeedback
  const sending = !sent && sendStatus.status === 'loading'
  const failed = !sent && sendStatus.status === 'failed'
  const disabled = sent || sending
  const isBad = isBadRating(values.stars)
  const emphasisReasons = target.type === 'shift' ? shiftEmphasisReasons : scheduleEmphasisReasons
  const errorReasons = target.type === 'shift' ? shiftErrorReasons : scheduleErrorReasons

  const onSend = () => dispatch(sendFeedback(toNewFeedback(draft, target)))

  return (
    <FooterPage
      footer={
        <FooterContainer>
          {sent ? (
            <SendButton
              sending={false}
              sent={true}
              title={t('close')}
              onClick={() => dispatch(visitURL(closeUrl))}
            />
          ) : (
            <SendButton
              sending={sending}
              sent={false}
              title={t(sending ? 'sendingFeedback' : 'sendFeedback')}
              onClick={sending ? noop : onSend}
            />
          )}
        </FooterContainer>
      }
      footerHeight={theme.maxHeights.footer}
    >
      <Header
        border={true}
        title={
          target.type === 'shift'
            ? t('shiftFeedback', { number: target.shiftNumber })
            : t('shiftListFeedback')
        }
        links={{
          prev: { url: closeUrl, disabled: false },
          next: null,
          close: { url: closeUrl, disabled: false },
        }}
      >
        {target.type === 'shift'
          ? moment(shift.startDateTime).format(MOMENT_SHIFT_DATE_FORMAT)
          : listDates(shift)}
      </Header>
      <Container>
        <Constrain>
          {failed ? (
            <Row>
              <ActionError error={sendStatus.error ?? ''} action={onSend} actionText="retry" />
            </Row>
          ) : undefined}
          <VerticalLayout>
            {disabled ? (
              <Rating stars={values.stars} />
            ) : (
              <RatingButtons
                stars={values.stars}
                onChange={(value) => dispatchDraft({ type: 'setStars', value })}
              />
            )}
            <Row key="question">
              <Label>
                {isBad ? t(`${target.type}WhatWasBad`) : t(`${target.type}WhatWasGood`)}
              </Label>
            </Row>
            {emphasisReasons.map((reason) => (
              <SelectableRow
                key={reason.value}
                disabled={disabled}
                onClick={() => dispatchDraft({ type: 'toggleEmphasis', value: reason.value })}
                title={t(`feedbackAnswer.${reason.title}`)}
                selected={values.emphasis.includes(reason.value)}
              />
            ))}
            <Row key="message">
              <LimitedTextarea
                value={values.message}
                disabled={disabled}
                onChange={(value) => dispatchDraft({ type: 'setMessage', value })}
              />
            </Row>
            {isBad ? (
              <>
                <Row key="questionErrors">
                  <Label>{t('didShiftContainErrors')}</Label>
                </Row>
                {errorReasons.map((reason) => (
                  <SelectableRow
                    key={reason.value}
                    disabled={disabled}
                    onClick={() => dispatchDraft({ type: 'toggleError', value: reason.value })}
                    title={t(`feedbackAnswer.${reason.title}`)}
                    selected={values.errors.includes(reason.value)}
                  />
                ))}
                <Row key="errorMessage">
                  <LimitedTextarea
                    value={values.errorMessage}
                    disabled={disabled}
                    onChange={(value) => dispatchDraft({ type: 'setErrorMessage', value })}
                  />
                </Row>
              </>
            ) : undefined}
            <SelectableRow
              key="needResponse"
              disabled={disabled}
              onClick={() => dispatchDraft({ type: 'toggleRequestAnswer' })}
              title={t('wantsResponse')}
              selected={values.requestAnswer}
            />
          </VerticalLayout>
        </Constrain>
      </Container>
    </FooterPage>
  )
}

export default FeedbackPage
